import React, { useState, useEffect } from "react"
// @ts-ignore
import { useParams, useHistory } from "react-router-dom"
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { TrashIcon2 } from "src/components/icons-svg"
import { Refresh as RefreshIcon } from "@material-ui/icons"
import useIsLoggedIn from "src/utils/use-is-logged-in"
import useStore from "src/v2/utils/store"
import { useAlertStore } from "src/v2/utils/alert-store"
import SimulationsHeader from "./simulations-header"
import SimulationCard from "./simulation-card"
import CustomCheckbox from "../../../components/form-components/custom-checkbox"
import CustomButton from "../../../components/form-components/custom-button"
import SimulationsFilter from "./simulations-filter"
import OneSimulation from "./one-simulation"

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  grid: {
    flex: 1,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(10),
    display: "flex",
    flexDirection: "column",
  },
  divider: {
    marginTop: 15,
    border: "1px solid #EAEAF2",
  },
  progressBox: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
    width: "100%",
    maxWidth: 600,
    margin: "0 auto",
  },
  actionsBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
    },
  },
  selectionBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  deleteIcon: {
    color: theme.palette.error.main,
    background: "#fff",
    borderRadius: "50%",
    boxShadow: "0 2px 6px rgba(0,0,0,0.07)",
    "&:hover": {
      background: theme.palette.error.light,
      color: "#fff",
    },
  },
  reRunContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    cursor: "pointer",
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  reRunIcon: {
    color: theme.palette.primary.main,
    animation: "$spin 1s linear infinite",
  },
  reRunIconStatic: {
    color: theme.palette.primary.main,
  },
  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
  reRunText: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    userSelect: "none",
  },
}))

export default function Simulations() {
  const classes = useStyles()
  const { isLoggedIn } = useIsLoggedIn()
  const history = useHistory()
  const [simulationLoading, setSimulationLoading] = useState(false)
  const [selectedSimulations, setSelectedSimulations] = useState([])
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState([]) // For single or multiple deletions
  const [isMultiDelete, setIsMultiDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Add new state for re-run functionality
  const [confirmReRunOpen, setConfirmReRunOpen] = useState(false)
  const [infoReRunOpen, setInfoReRunOpen] = useState(false)
  const [isReRunning, setIsReRunning] = useState(false)

  const simulations = useStore((s) => s.simulations)
  const filteredSimulations = useStore((s) => s.filteredSimulations)
  const fetchSimulations = useStore((s) => s.fetchSimulations)
  const deleteSimulation = useStore((s) => s.deleteSimulation)
  const reRunAllSimulations = useStore((s) => s.reRunAllSimulations)
  const createLog = useStore((s) => s.createLog)
  const showAlert = useAlertStore((state) => state.showAlert)
  const isSimulationDetailView = useStore((s) => s.isSimulationDetailView)
  const showSimulationDetail = useStore((s) => s.showSimulationDetail)
  const selectedSimulationId = useStore((s) => s.selectedSimulationId)
  const setSimulationDetailView = useStore((s) => s.setSimulationDetailView)

  const { reviewId } = useParams()

  // Fetch simulations on component mount or reviewId change
  useEffect(() => {
    const fetchData = async () => {
      setSimulationLoading(true)
      if (reviewId) {
        await fetchSimulations(reviewId)
      }
      setSimulationLoading(false)
    }
    fetchData()
    // Reset selection when simulations change
    setSelectedSimulations([])
  }, [reviewId, fetchSimulations])

  // Check if all simulations are selected
  const allSelected =
    simulations.length > 0 && selectedSimulations.length === simulations.length

  // Check if some but not all simulations are selected
  const someSelected =
    selectedSimulations.length > 0 &&
    selectedSimulations.length < simulations.length

  // Handle selecting/deselecting a single simulation
  const handleSelect = (simulationId) => {
    setSelectedSimulations((prev) => {
      if (prev.includes(simulationId)) {
        return prev.filter((id) => id !== simulationId)
      } else {
        return [...prev, simulationId]
      }
    })
  }

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSimulations(simulations.map((sim) => sim._id))
    } else {
      setSelectedSimulations([])
    }
  }

  // Handle delete for single simulation
  const handleDeleteOne = (simulationId) => {
    setDeleteTarget([simulationId])
    setIsMultiDelete(false)
    setConfirmDeleteOpen(true)
  }

  // Handle bulk delete
  const handleDeleteSelected = () => {
    setDeleteTarget(selectedSimulations)
    setIsMultiDelete(true)
    setConfirmDeleteOpen(true)
  }

  // Handle refresh after deletion (can be passed to SimulationCard)
  const refreshSimulations = () => {
    if (reviewId) {
      fetchSimulations(reviewId)
    }
  }

  // Add new handlers for re-run functionality
  const handleReRunAllClick = () => {
    if (isReRunning || simulations.length === 0) return
    createLog({
      event_type: 'Review.ReRunSimulations',
      operation: 'Re-run All Simulations',
      review_id: reviewId,
      metadata: { totalSimulations: simulations ? simulations.length : 0 },
    })
    setConfirmReRunOpen(true)
  }

  const handleConfirmReRun = () => {
    setConfirmReRunOpen(false)
    setInfoReRunOpen(true)
  }

  const handleStartReRun = async () => {
    setInfoReRunOpen(false)
    setIsReRunning(true)

    try {
      const result = await reRunAllSimulations(reviewId, showAlert)
      
      if (result && result.success) {
        showAlert({
          message: "Re-run successful",
          severity: "success",
          title: "Success",
        })
        
        // Refresh simulations data
        refreshSimulations()
      } else {
        throw new Error(result?.error || "Re-run failed")
      }
    } catch (error) {
      console.error("Error re-running simulations:", error)
      createLog({
        event_type: 'Review.ReRunSimulations',
        operation: 'Re-run All Simulations',
        review_id: reviewId,
        result: 'failure',
        http_status: 500,
        error_message: error && error.message,
      })
      showAlert({
        message: error.message || "Failed to re-run simulations",
        severity: "error",
        title: "Error",
      })
    } finally {
      setIsReRunning(false)
    }
  }

  // Confirm deletion (either single or multiple)
  const confirmDelete = async () => {
    try {
      setIsDeleting(true)

      if (deleteTarget.length > 0) {
        // Use the store's deleteSimulation function
        const result = await deleteSimulation(deleteTarget, showAlert)

        if (result && result.success) {
          // Clear selections that were deleted
          setSelectedSimulations((prev) =>
            prev.filter((id) => !deleteTarget.includes(id)),
          )

          // Refresh simulations data
          refreshSimulations()
        }
      }
    } catch (error) {
      console.error("Error deleting simulation(s):", error)
      // Error handling is done within deleteSimulation with showAlert
    } finally {
      setConfirmDeleteOpen(false)
      setIsDeleting(false)
    }
  }

  // Update the handle click function
  const handleSimulationClick = (simulation) => {
    showSimulationDetail(simulation._id)
  }

  // If we're in detail view, show the Simulation component instead
  if (isSimulationDetailView) {
    return (
      <OneSimulation
        simulationId={selectedSimulationId}
        onClose={() => {
          setSimulationDetailView(false)
          refreshSimulations()
        }}
      />
    )
  }

  return (
    <Box className={classes.container}>
      <SimulationsHeader />

      {/* Add the SimulationsFilter component */}
      {!simulationLoading && simulations.length > 0 && <SimulationsFilter />}

      {simulationLoading ? (
        <Box className={classes.progressBox}>
          <CircularProgress />
        </Box>
      ) : (
        <Box className={classes.grid}>
          {/* Selection and actions bar - only show if there are simulations */}
          {simulations.length > 0 && (
            <Box className={classes.actionsBar}>
              <Box className={classes.selectionBar}>
                <CustomCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  label="Select All"
                  onChange={(checked) => handleSelectAll(checked)}
                  disabled={simulations.length === 0}
                />

                {selectedSimulations.length > 0 && (
                  <>
                    <IconButton
                      className={classes.deleteIcon}
                      onClick={handleDeleteSelected}
                      disabled={isDeleting}
                    >
                      {TrashIcon2(20)}
                    </IconButton>
                    <Typography variant="body2">
                      Delete Selected ({selectedSimulations.length})
                    </Typography>
                  </>
                )}
              </Box>

              {/* Re-run All with icon and text */}
              <Box
                className={classes.reRunContainer}
                onClick={handleReRunAllClick}
                style={{
                  pointerEvents: isReRunning || simulations.length === 0 ? "none" : "auto",
                  opacity: isReRunning || simulations.length === 0 ? 0.6 : 1,
                }}
              >
                <RefreshIcon 
                  className={isReRunning ? classes.reRunIcon : classes.reRunIconStatic}
                />
                <Typography className={classes.reRunText}>
                  {isReRunning ? "Re-running..." : "Re-run All"}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Simulation cards grid - use filteredSimulations instead of simulations */}
          {filteredSimulations.length > 0 ? (
            <Grid container spacing={3}>
              {filteredSimulations.map((item, index) => (
                <Grid item xs={12} sm={6} md={6} lg={4} key={item._id || index}>
                  <SimulationCard
                    simulation={item}
                    onClick={() => handleSimulationClick(item)}
                    onDelete={handleDeleteOne}
                    selected={selectedSimulations.includes(item._id)}
                    onSelect={handleSelect}
                    onRefresh={refreshSimulations}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              color="textSecondary"
              style={{ textAlign: "center", marginTop: 40 }}
            >
              {simulations.length > 0
                ? "No matching simulations found."
                : "No simulations found."}
            </Typography>
          )}
        </Box>
      )}

      {/* Replace CustomModal with Dialog to match the review card's delete confirmation */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !isDeleting && setConfirmDeleteOpen(false)}
        onClick={(e) => e.stopPropagation()} // Prevent card click
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {isMultiDelete
              ? `Are you sure you want to delete ${deleteTarget.length} selected simulations?`
              : "Are you sure you want to delete this simulation? This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setConfirmDeleteOpen(false)}
            variant="link"
            disabled={isDeleting}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={confirmDelete}
            color="primary"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </CustomButton>
        </DialogActions>
      </Dialog>

      {/* Re-run confirmation dialog */}
      <Dialog
        open={confirmReRunOpen}
        onClose={() => setConfirmReRunOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Re-run</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to re-run all simulations?
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={() => setConfirmReRunOpen(false)}
            variant="link"
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleConfirmReRun}
            color="primary"
            variant="contained"
          >
            Yes, Re-run
          </CustomButton>
        </DialogActions>
      </Dialog>

      {/* Re-run info dialog */}
      <Dialog
        open={infoReRunOpen}
        onClose={() => setInfoReRunOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Important Information</DialogTitle>
        <DialogContent>
          <Typography>
            After pressing OK it will start. Please do not close the page until you get a DONE message.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleStartReRun}
            color="primary"
            variant="contained"
          >
            OK
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
